import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Lock, User } from 'lucide-react';
import AuthShell from '../components/layout/AuthShell';
import Input from '../components/ui/Input';
import Button from '../components/ui/Button';
import SocialLogins from '../components/auth/SocialLogins';
import { useAuth, getErrorMessage } from '../context/AuthContext';
import { useToast } from '../components/ui/Toast';

export default function Signup() {
  const { signup, googleLogin } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const [form, setForm] = useState({ name: '', email: '', password: '', confirm: '' });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const e = {};
    if (form.name.trim().length < 2) e.name = 'Tell us your name.';
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) e.email = 'Enter a valid email.';
    if (form.password.length < 8) e.password = 'At least 8 characters.';
    if (form.confirm !== form.password) e.confirm = 'Passwords do not match.';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const data = await signup({ name: form.name.trim(), email: form.email, password: form.password });
      if (data?.verifyRequired) {
        toast.success('Check your email for a 6-digit code.');
        navigate('/verify-email', { state: { email: data.email, devCode: data.devCode } });
      }
    } catch (err) {
      toast.error(getErrorMessage(err, 'Could not create account.'));
    } finally {
      setSubmitting(false);
    }
  };

  const onGoogle = async (credential) => {
    try {
      await googleLogin(credential);
      toast.success('Welcome to Aether!');
      navigate('/app', { replace: true });
    } catch (err) {
      toast.error(getErrorMessage(err, 'Google sign-in failed.'));
    }
  };

  return (
    <AuthShell
      title="Create your account"
      subtitle="Start generating in under a minute. No card required."
      footer={
        <>
          Already have an account?{' '}
          <Link to="/login" className="font-medium text-brand-300 hover:text-brand-200">
            Log in
          </Link>
        </>
      }
    >
      <SocialLogins onGoogle={onGoogle} />

      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        <Input name="name" label="Name" placeholder="Ada Lovelace" autoComplete="name" icon={User} value={form.name} onChange={set('name')} error={errors.name} />
        <Input name="email" type="email" label="Email" placeholder="you@example.com" autoComplete="email" icon={Mail} value={form.email} onChange={set('email')} error={errors.email} />
        <Input name="password" type="password" label="Password" placeholder="At least 8 characters" autoComplete="new-password" icon={Lock} value={form.password} onChange={set('password')} error={errors.password} />
        <Input name="confirm" type="password" label="Confirm password" placeholder="Re-enter password" autoComplete="new-password" icon={Lock} value={form.confirm} onChange={set('confirm')} error={errors.confirm} />
        <Button type="submit" className="w-full" loading={submitting} size="lg">
          Create account
        </Button>
        <p className="text-center text-xs text-content-subtle">
          By signing up you agree to our Terms and Privacy Policy.
        </p>
      </form>
    </AuthShell>
  );
}
